/**
 * Hooks para Histórico de Qualificações e Habilitações (Módulo 2)
 * Endpoints: /api/qualificacoes/historico, /api/qualificacoes/habilitacoes
 */

import { useApi } from './useApi';
import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';

export interface HistoricoQualificacao {
  id: number;
  funcionario_id: number;
  qualificacao_id: number;
  status?: string;
  validade?: string | number; // pode ser string (data) ou number (meses)
  data_registro: string;
  renovada?: number | boolean; // Flag de qualificação renovada (1 = renovada, 0 = não)
  funcionario_nome: string;
  funcionario_guerra?: string;
  funcionario_matricula?: string;
  funcionario_codigo_anac?: string;
  funcionario_cargo?: string;
  funcionario_funcao?: string;
  funcionario_setor?: string;
  funcionario_base?: string;
  funcionario_aeronave?: string;
  funcionario_admissao?: string;
  funcionario_is_instrutor?: number | boolean;
  funcionario_is_checador?: number | boolean;
  funcionario_status?: string;
  funcionario_ativo?: number | boolean;
  qualificacao_desc: string;
  qualificacao_nome?: string;
  qualificacao_codigo?: string;
  qualificacao_categoria?: string;
  qualificacao_validade?: number; // agregado da view integrada (meses)
  validade_meses?: number; // validade em meses do tipo de qualificação
  vencimento_fim_mes?: number; // 0 = dia exato, 1 = fim do mês
  categoria?: string;
  tipo?: string;
  codigo?: string;
  data_realizacao?: string; // alias para data_conclusao (backend usa este nome)
  data_conclusao?: string;
  data_emissao?: string;
  data_vencimento?: string;
  data_validade?: string;
  certificado_numero?: string;
  certificado_url?: string;
  certificado_nome?: string;
  observacoes?: string;
  nota?: number;
  resultado?: string;
  instrutor?: string;
  local?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Habilitacao {
  id: number;
  funcionario_id: number;
  tipo: string;
  numero: string;
  validade: string;
  created_at: string;
}

// Novo tipo para Qualificação Tipo (tipos de qualificação)
export interface QualificacaoTipoDTO {
  id: string | number;
  nome: string;
  codigo?: string | null;
  categoria?: string | null;
  descricao?: string | null;
  validade?: number | null;
  observacoes?: string | null;
  ativo?: number | boolean | null;
  created_at?: string;
  updated_at?: string | null;
}

interface ApiResponse {
  success?: boolean;
  data?: HistoricoQualificacao[]; // payload principal
  meta?: {
    // espelha backend unificado
    page: number;
    limit: number;
    total: number;
    total_pages: number;
    minimal?: boolean;
    has_data?: boolean;
  };
  stats?: {
    total: number;
    validas: number;
    vencendo: number;
    vencidas: number;
    renovadas?: number;
  };
}

/**
 * Hook para listar histórico de qualificações com estatísticas calculadas
 * @param funcionarioId Filtro opcional por funcionário
 * @param limit Quantidade máxima de resultados (padrão: 50, máx: 500)
 * @param page Página atual (padrão: 1)
 */
export function useQualificacoesHistorico(
  funcionarioId?: number,
  limit = 50,
  page = 1,
  includeStats: boolean = true,
) {
  const [loadingExtra, setLoadingExtra] = useState(false);
  const safeLimit = Math.min(limit, 500); // Máximo 500 por página

  let endpoint = `/api/qualificacoes/historico?limit=${safeLimit}&page=${page}`;
  if (!includeStats) endpoint += `&stats=false`;
  if (funcionarioId) endpoint += `&funcionario_id=${funcionarioId}`;

  const { data, loading, error, refetch } = useApi<ApiResponse>(endpoint, {
    enabled: true,
    requireAuth: true, // exige token para garantir acesso correto
  });

  // 🔄 Buscar stats corretos do endpoint de dashboard (com renovadas calculadas pelo backend)
  const { data: dashboardData } = useApi<{
    success: boolean;
    data: {
      total_ativas: number;
      vencidas: number;
      a_vencer_30_dias: number;
      validas: number;
      renovadas: number;
    };
  }>('/api/dashboard/qualificacoes', {
    enabled: includeStats,
    requireAuth: true,
  });

  const carregarHistorico = useCallback(() => refetch(), [refetch]);

  const renovarQualificacao = useCallback(
    async (idAntiga: number, novaDataVencimento: string): Promise<HistoricoQualificacao | null> => {
      try {
        setLoadingExtra(true);
        const resp = await fetch(`/api/qualificacoes/historico/${idAntiga}/renovar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nova_data_vencimento: novaDataVencimento }),
        });
        const json = await resp.json();
        if (!resp.ok || !json.success) {
          throw new Error(json.error || 'Falha na renovação');
        }
        toast.success('Qualificação renovada com sucesso');
        await carregarHistorico();
        return json.data?.novo || null;
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Erro desconhecido';
        toast.error(msg);
        return null;
      } finally {
        setLoadingExtra(false);
      }
    },
    [carregarHistorico],
  );

  // Extrai o array de histórico de forma segura, independentemente do formato da resposta
  // Backend retorna objeto { success, data, meta, stats }
  console.log('🔍 [useQualificacoesHistorico] Response completo:', data);
  console.log('🔍 [useQualificacoesHistorico] data.stats:', (data as { stats?: unknown })?.stats);
  console.log('🔍 [useQualificacoesHistorico] data.meta:', (data as { meta?: unknown })?.meta);

  const historicoRaw: HistoricoQualificacao[] = Array.isArray(data)
    ? (data as unknown as HistoricoQualificacao[])
    : Array.isArray(data?.data)
    ? (data.data as HistoricoQualificacao[])
    : [];

  // Enriquecer com status calculado (RENOVADA, VALIDA, PROXIMA_VENCIMENTO, VENCIDA) para UI e KPIs
  const hoje = Date.now();
  const diasLimiteVencendo = 30; // janela de proximidade de vencimento

  console.log('🔍 [useQualificacoesHistorico] Processando', historicoRaw.length, 'registros');
  console.log('🔍 [useQualificacoesHistorico] Primeiro registro raw:', historicoRaw[0]);

  const historico: HistoricoQualificacao[] = historicoRaw.map((h) => {
    // DEBUG: Log do campo renovada
    if (h.renovada === 1) {
      console.log('✅ [RENOVADA] Encontrado:', {
        id: h.id,
        renovada: h.renovada,
        qualificacao: h.qualificacao_nome,
        funcionario: h.funcionario_nome,
      });
    }

    // PRIORIDADE 1: Se renovada = 1, é RENOVADA (não importa vencimento)
    if (h.renovada === 1) {
      return { ...h, status: 'RENOVADA' };
    }

    // PRIORIDADE 2: Calcular status baseado em vencimento
    if (!h.data_vencimento) return { ...h, status: 'INDETERMINADA' };
    const tsVenc = new Date(h.data_vencimento).getTime();
    if (isNaN(tsVenc)) return { ...h, status: 'INDETERMINADA' };
    const diffDias = Math.floor((tsVenc - hoje) / (1000 * 60 * 60 * 24));

    let status: string;
    if (diffDias < 0) status = 'VENCIDA';
    else if (diffDias <= diasLimiteVencendo) status = 'PROXIMA_VENCIMENTO';
    else status = 'VALIDA';

    return { ...h, status };
  });

  console.log('📊 [useQualificacoesHistorico] Status calculados:', {
    total: historico.length,
    validas: historico.filter((q) => q.status === 'VALIDA').length,
    vencendo: historico.filter((q) => q.status === 'PROXIMA_VENCIMENTO').length,
    vencidas: historico.filter((q) => q.status === 'VENCIDA').length,
    renovadas: historico.filter((q) => q.status === 'RENOVADA').length,
  });

  const meta = data?.meta;

  // 🎯 Usar stats do endpoint de dashboard (com renovadas corretas) ou calcular localmente
  const stats =
    includeStats && dashboardData?.data
      ? {
          total: dashboardData.data.total_ativas,
          validas: dashboardData.data.validas,
          vencendo: dashboardData.data.a_vencer_30_dias,
          vencidas: dashboardData.data.vencidas,
          renovadas: dashboardData.data.renovadas, // ✅ Valor correto do backend
        }
      : {
          total: historico.length,
          validas: historico.filter((q) => q.status === 'VALIDA').length,
          vencendo: historico.filter((q) => q.status === 'PROXIMA_VENCIMENTO').length,
          vencidas: historico.filter((q) => q.status === 'VENCIDA').length,
          renovadas: historico.filter((q) => q.renovada === 1 || q.status === 'RENOVADA').length,
        };

  console.log('📊 [useQualificacoesHistorico] Stats finais usados:', stats);
  console.log('📊 [useQualificacoesHistorico] Dashboard data:', dashboardData?.data);

  return {
    historico,
    stats,
    meta,
    loading: loading || loadingExtra,
    error,
    refetch,
    carregarHistorico,
    renovarQualificacao,
  };
}

/**
 * Hook para listar habilitações (CPL, INVA, etc)
 * @param limit Quantidade máxima de resultados (padrão: 50)
 */
export function useHabilitacoes(limit = 50) {
  const { data, loading, error, refetch } = useApi<Habilitacao[]>(
    `/api/qualificacoes/habilitacoes?limit=${limit}`,
    { enabled: true },
  );

  return {
    habilitacoes: data || [],
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para listar tipos de qualificações
 * Normaliza campo obrigatoria para boolean e garante array segura
 */
export function useQualificacaoTipos(enabled: boolean = true, limit = 200) {
  const { data, loading, error, refetch } = useApi<{ data?: QualificacaoTipoDTO[] }>(
    `/api/qualificacoes/tipos?limit=${limit}`,
    {
      enabled,
      requireAuth: true,
    },
  );

  // A API pode retornar { success: true, data: [...] } ou apenas [...]
  const tipos: QualificacaoTipoDTO[] = Array.isArray(data)
    ? data
    : data?.data && Array.isArray(data.data)
    ? data.data
    : [];

  return {
    tipos,
    loading,
    error,
    refetch,
  };
}
